import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Button, Typography, Pill} from 'views/components';
import {translate} from 'views/module';

import ClientFeHandoff from '../ClientFeHandoff';
import StageDropdown from './StageDropdown';

import {formatDate, formatDateTime} from '../../format';
import moment from 'moment';

const nullChar = '-';

const stageReasonLabel = (statusLog) => {
  if (statusLog?.lostReason) {
    return translate('loomLead.ChangeStatus.Pill.LostReason', {
      value: translate(`loomLead.ClosedLostReason.${statusLog.lostReason}.Title`),
    });
  }

  if (statusLog?.freeTextStatusReason) {
    return translate('loomLead.ChangeStatus.Pill.InactiveReason', {value: statusLog.freeTextStatusReason});
  }

  return null;
};

// The chips and the details grid under the client name. Stage is the only dropdown; there is no
// Book call cell.
const ClientDetails = (props) => {
  const {
    clientData,
    clientCareplan,
    setRefresh,
  } = props;

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());

  const stage = clientData.status;
  const stageReason = stageReasonLabel(clientData.clientLastLogStatusUpdate);
  const leadQuality = clientCareplan?.leadQuality;
  // The Loom adapter does not supply an intro call status yet, so this cell shows the null value
  // until it does.
  const callStatus = clientData.callStatus;

  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(clientData.relationships ?? {}).map((relationship) => {
      const email = relationship.contacts?.find((x) => x?.type?.toLowerCase().includes('mail'))?.detail ?? '';
      const phoneNumber = relationship.contacts?.find((x) =>
        x?.type?.toLowerCase().includes('phone') ||
        x?.type?.toLowerCase().includes('number'),
      )?.detail ?? '';

      return {
        ...relationship,
        email,
        phoneNumber,
      };
    });

    return relationships.sort((a, b) => {
      if ((a.priority ?? Number.MAX_SAFE_INTEGER) !== (b.priority ?? Number.MAX_SAFE_INTEGER)) {
        return (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER);
      }

      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [clientData.relationships]);
  const primaryRelationship = clientRelationships[0] ?? null;

  const clientCreatedAt = clientData.createdAt ?? null;
  const daysFromCreation = React.useMemo(() => {
    if (!clientCreatedAt) return 0;
    return moment().diff(moment(clientCreatedAt), 'days');
  }, [clientCreatedAt]);
  const clientLocation = clientCareplan?.generalInformation?.address ? clientCareplan?.generalInformation?.address : nullChar;

  const handleRefresh = () => {
    setRefresh((value) => value + 1);
    setLastUpdatedAt(new Date().toISOString());
  };

  const metricsSections = React.useMemo(() => [
    {
      key: 'primaryContact',
      label: translate('loomLead.LoomLeadDetail.Header.Metrics.PrimaryContact'),
      value: primaryRelationship?.name || nullChar,
      cellSx: {
        justifyContent: {
          xs: 'center',
          xl: 'flex-start',
        },
      },
    },
    {
      key: 'contactEmail',
      label: translate('loomLead.LoomLeadDetail.Header.Metrics.ContactEmail'),
      value: primaryRelationship?.email || nullChar,
    },
    {
      key: 'contactPhone',
      label: translate('loomLead.LoomLeadDetail.Header.Metrics.ContactPhone'),
      value: primaryRelationship?.phoneNumber || nullChar,
    },
    {
      key: 'stage',
      type: 'stageDropdown',
      label: translate('loomLead.LoomLeadDetail.Header.Menu.Subtitles.Stage'),
    },
    {
      key: 'leadQuality',
      label: translate('loomLead.LoomLeadDetail.Header.Menu.Subtitles.LeadQuality'),
      value: leadQuality ? translate(`loomLead.LeadQuality.${leadQuality}.Title`) : nullChar,
    },
    {
      key: 'introCall',
      label: translate('loomLead.LoomLeadDetail.Header.Metrics.IntroCall'),
      value: callStatus ? translate(`loomLead.CallStatus.${callStatus}.Title`) : nullChar,
      cellSx: {
        justifyContent: {
          xs: 'center',
          xl: 'flex-end',
        },
      },
    },
  ], [callStatus, leadQuality, primaryRelationship]);

  return (
    <Stack sx={{gap: '10px'}}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        sx={{gap: '10px', flexWrap: 'wrap'}}
      >
        <Stack
          direction='row'
          sx={{
            gap: '7px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Pill
            label={translate('loomLead.LoomLeadDetail.Header.Subtitles.Day', {number: daysFromCreation})}
            variant='info'
          />
          {
            clientLocation !== nullChar &&
            <Pill
              label={clientLocation}
              variant='neutral'
            />
          }
          {
            clientCreatedAt &&
            <Pill
              label={translate('loomLead.LoomLeadDetail.Header.Subtitles.Created', {
                value: formatDate(clientCreatedAt),
              })}
              variant='neutral'
            />
          }
          <Pill
            label={translate('loomLead.LoomLeadDetail.Header.Subtitles.Updated', {
              value: formatDateTime(lastUpdatedAt),
            })}
            variant='neutral'
          />
          {
            stageReason &&
            <Pill
              label={stageReason}
              title={stageReason}
              variant='neutral'
              sx={{maxWidth: '320px'}}
              labelSx={{overflow: 'hidden', textOverflow: 'ellipsis'}}
            />
          }
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          sx={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}
        >
          <ClientFeHandoff key={clientData.clientId} clientData={clientData} setRefresh={setRefresh} />
          <Button
            variant='outlined'
            onClick={handleRefresh}
            sx={{
              'boxSizing': 'border-box',
              'minWidth': '63px',
              'height': '32px',
              'px': '10px',
              'py': '8px',
              'border': '1px solid #D8D3CC',
              'borderRadius': '7px',
              'backgroundColor': '#FFFFFF',
              'color': '#6B6760',
              'boxShadow': 'none',
              'textTransform': 'none',
              '&:hover': {
                'border': '1px solid #D8D3CC',
                'backgroundColor': '#F7F4EF',
                'boxShadow': 'none',
              },
              '&:disabled': {
                'border': '1px solid #E7E2DB',
                'backgroundColor': '#F8F6F2',
                'color': '#B4AEA6',
              },
            }}
          >
            <Typography
              sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: 'inherit',
              }}
            >
              {translate('generic.Refresh')}
            </Typography>
          </Button>
        </Stack>
      </Stack>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#E8E4DE',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: 'repeat(2, minmax(0, auto))',
              md: 'repeat(3, minmax(0, auto))',
              xl: `repeat(${metricsSections.length}, minmax(0, auto))`,
            },
          }}
        >
          {
            metricsSections.map((section) => {
              return (
                <Box
                  key={section.key}
                  sx={{
                    minWidth: 0,
                    backgroundColor: '#FFFFFF',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...section.cellSx,
                  }}
                >
                  {
                    section.type === 'stageDropdown' &&
                    <StageDropdown
                      clientId={clientData.clientId}
                      label={section.label}
                      selectedStage={stage}
                      onChanged={() => setRefresh((value) => value + 1)}
                    />
                  }
                  {
                    !section.type &&
                    <Stack
                      sx={{
                        gap: '6px',
                        minWidth: 0,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: '#A8A49E',
                        }}
                      >
                        {section.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#000000',
                          wordBreak: 'break-word',
                        }}
                      >
                        {section.value}
                      </Typography>
                    </Stack>
                  }
                </Box>
              );
            })
          }
        </Box>
      </Box>
    </Stack>
  );
};
ClientDetails.propTypes = {
  clientData: PropTypes.object.isRequired,
  clientCareplan: PropTypes.object,
  setRefresh: PropTypes.func.isRequired,
};

export default React.memo(ClientDetails);
